import {
  Check,
  HomeIcon,
  LockOpenIcon,
  LogOutIcon,
  PlusIcon,
} from 'lucide-react';
import { MouseEventHandler, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ElementOf } from 'ts-essentials';

import { useAuth } from '@dotfile/frontend/shared/common';
import {
  Box,
  Icon,
  MenuDivider,
  MenuItem,
  MenuItemOption,
  MenuList,
  MenuOptionGroup,
  SkeletonText,
  useToast,
} from '@dotfile/frontend/shared/design-system';
import { useCaseFormsQuery } from '@dotfile/shared/data-access-client-portal';
import { CaseStatusEnum, ContactStateEnum } from '@dotfile/shared/domain';

import { useSwitchCase } from '../../../modules/onboardings/hooks';
import { useRouter } from '../../../routes';
import { ContactAuthContext } from '../../contexts';
import {
  useContactCases,
  useCurrentContactEmail,
  useLogout,
} from '../../hooks';

export const CaseMenuList = () => {
  const { t } = useTranslation();
  const router = useRouter();
  const toast = useToast();
  const { auth, requestMagicLink } = useAuth<ContactAuthContext>();
  const { data: caseData } = useCaseFormsQuery({
    variables: {
      id: auth.caseId as string,
    },
    skip: !auth.caseId,
  });
  const { email } = useCurrentContactEmail();

  const { data: otherCaseData, loading: otherCaseLoading } = useContactCases();
  const [switchCase, { loading }] = useSwitchCase();

  const options = useMemo(() => {
    const filteredCases = (otherCaseData?.contactCases ?? []).filter(
      (otherCase) =>
        otherCase.contactHasActions ||
        otherCase.status === CaseStatusEnum.draft,
    );
    const options = filteredCases.map((otherCase) => ({
      value: otherCase.id,
      label: otherCase.name,
    }));
    return options;
  }, [otherCaseData]);

  const [logout] = useLogout();

  const onMyOnboarding: MouseEventHandler<HTMLButtonElement> = (event) => {
    event.preventDefault();
    router.push('Onboardings');
  };

  const onCaseSelect =
    (option: ElementOf<typeof options>): MouseEventHandler<HTMLButtonElement> =>
    async (event) => {
      event.preventDefault();
      await switchCase({
        caseId: option.value,
      });
    };
  const onVerifyEmail = async () => {
    const currentContactEmail = caseData?.case?.individuals.find(
      (individual) => individual.id === auth.contactId,
    )?.email;
    if (currentContactEmail) {
      await requestMagicLink(currentContactEmail);
      toast({
        title: t('onboardings.email_verification.email_sent_title', {
          ns: 'client-portal',
          defaultValue: 'Email sent',
        }),
        description: t(
          'onboardings.email_verification.email_sent_description',
          {
            ns: 'client-portal',
            defaultValue: 'An email has been sent to verify your email address',
          },
        ),
        status: 'success',
      });
    }
  };

  const isResumed = auth.state === ContactStateEnum.resumed;

  return (
    <MenuList>
      {isResumed ? (
        <>
          <MenuItem onClick={onMyOnboarding} icon={<HomeIcon size="20" />}>
            {t(`onboardings.title`, {
              ns: 'client-portal',
              defaultValue: 'My onboardings',
            })}
          </MenuItem>
          <MenuDivider />

          {otherCaseLoading ? (
            <>
              <MenuItem>
                <SkeletonText noOfLines={1} w="100px" h="32px" />
              </MenuItem>
              <MenuItem>
                <SkeletonText noOfLines={1} w="100px" h="32px" />
              </MenuItem>
              <MenuItem>
                <SkeletonText noOfLines={1} w="100px" h="32px" />
              </MenuItem>
            </>
          ) : (
            <MenuOptionGroup
              type="radio"
              defaultValue={auth.caseId ?? undefined}
            >
              <Box
                maxHeight="256px" // scroll when there is more than 6 cases and show a bit of the seventh
                overflow="auto"
              >
                {options.map((option) => (
                  <MenuItemOption
                    key={option.value}
                    alignItems="center"
                    noOfLines={1}
                    value={option.value ?? undefined}
                    isChecked={option.value === auth.caseId}
                    display="flex"
                    sx={{
                      _disabled: {
                        color: 'inherit',
                        pointerEvents: 'none',
                        cursor: 'not-allowed',
                      },
                    }}
                    icon={<Icon as={Check} />}
                    onClick={onCaseSelect(option)}
                  >
                    {option.label}
                  </MenuItemOption>
                ))}
              </Box>
            </MenuOptionGroup>
          )}
        </>
      ) : (
        <MenuItem onClick={onVerifyEmail} icon={<LockOpenIcon size="20" />}>
          {t('onboardings.email_verification.verify_email', {
            ns: 'client-portal',
            defaultValue: 'Verify email to access my onboardings',
          })}
        </MenuItem>
      )}
      {(otherCaseLoading || options.length > 0 || !isResumed) && (
        <MenuDivider />
      )}
      <MenuItem
        isDisabled={loading}
        onClick={async () => {
          await logout({
            to: 'Forms',
            afterLogout: (state) => {
              state.addIndividualData({ email });
            },
          });
        }}
        icon={<PlusIcon size="20" />}
      >
        {t('login.start_new', {
          ns: 'client-portal',
          defaultValue: 'Start new onboarding ',
        })}
      </MenuItem>
      <MenuItem
        isDisabled={loading}
        onClick={() => logout()}
        icon={<LogOutIcon size="20" />}
      >
        {t('logout', {
          ns: 'client-portal',
          defaultValue: 'Log out',
        })}
      </MenuItem>
    </MenuList>
  );
};
